import type { ExternalRegisterAlertType, ExternalRegisterSummary } from "./types";
import i18n from "@/i18n";

const FALLBACK_ALERT_COLOR = "#b3261e";

const EXTERNAL_REGISTER_ALERT_TYPE_LABEL_KEYS: Record<ExternalRegisterAlertType, { key: string; defaultValue: string }> = {
  SECURITY_PLAN: { key: "externalRegister.alertTypes.securityPlan", defaultValue: "Security plan" },
  MANAGEMENT_PLAN: { key: "externalRegister.alertTypes.managementPlan", defaultValue: "Management plan" },
  FAST_TRACK_MEDICAL: { key: "externalRegister.alertTypes.fastTrackMedical", defaultValue: "Fast-track medical" },
  CHILD_PROTECTION: { key: "externalRegister.alertTypes.childProtection", defaultValue: "Child protection" },
  OTHER: { key: "externalRegister.alertTypes.other", defaultValue: "Other" },
};

export const DEFAULT_EXTERNAL_REGISTER_ALERT_TYPE_COLORS: Record<ExternalRegisterAlertType, string> = {
  SECURITY_PLAN: "#000000",
  MANAGEMENT_PLAN: "#64748B",
  FAST_TRACK_MEDICAL: "#64748B",
  CHILD_PROTECTION: "#64748B",
  OTHER: "#64748B",
};

export type ExternalRegisterAlertPresentation = {
  type: ExternalRegisterAlertType;
  label: string;
  color: string;
};

/**
 * Central presentation projection for External Register markers. Use this for
 * both subtle pre-authorization dots and post-authorization secure details so
 * type colours do not drift between surfaces.
 */
export function getExternalRegisterAlertPresentation(
  alert: Pick<ExternalRegisterSummary, "type" | "color">,
): ExternalRegisterAlertPresentation {
  return {
    type: alert.type,
    label: getExternalRegisterAlertTypeLabel(alert.type),
    color: alert.color?.trim() || DEFAULT_EXTERNAL_REGISTER_ALERT_TYPE_COLORS[alert.type] || FALLBACK_ALERT_COLOR,
  };
}

export function getExternalRegisterAlertColor(alert: Pick<ExternalRegisterSummary, "type" | "color">): string {
  return getExternalRegisterAlertPresentation(alert).color;
}

export function getExternalRegisterAlertTypeLabel(type: ExternalRegisterAlertType): string {
  const label = EXTERNAL_REGISTER_ALERT_TYPE_LABEL_KEYS[type] ?? EXTERNAL_REGISTER_ALERT_TYPE_LABEL_KEYS.OTHER;
  return i18n.t(label.key, { defaultValue: label.defaultValue });
}

export function getActiveExternalRegisterAlerts(alerts?: ExternalRegisterSummary[] | null): ExternalRegisterSummary[] {
  return Array.isArray(alerts) ? alerts.filter((alert) => alert.status === "active") : [];
}

export function hasActiveExternalRegisterAlert(alerts?: ExternalRegisterSummary[] | null): boolean {
  return getActiveExternalRegisterAlerts(alerts).length > 0;
}

export type ExternalRegisterTypeIndicatorDot = {
  type: ExternalRegisterAlertType;
  color: string;
  alertIds: string[];
};

/**
 * Pre-authorization External Register surfaces deliberately communicate only
 * "a sensitive clinical marker exists" via colour. Multiple entries of the
 * same type collapse to one dot so every subtle surface follows the same
 * privacy-preserving projection.
 */
export function getExternalRegisterTypeIndicatorDots(
  alerts?: ExternalRegisterSummary[] | null,
): ExternalRegisterTypeIndicatorDot[] {
  const dotsByType = new Map<ExternalRegisterAlertType, ExternalRegisterTypeIndicatorDot>();

  getActiveExternalRegisterAlerts(alerts).forEach((alert) => {
    const existing = dotsByType.get(alert.type);
    if (existing) {
      existing.alertIds.push(alert.id);
      return;
    }

    dotsByType.set(alert.type, {
      type: alert.type,
      color: getExternalRegisterAlertColor(alert),
      alertIds: [alert.id],
    });
  });

  return Array.from(dotsByType.values());
}
